import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export const LoginModal = ({ open }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  if (!open) return null;
  const ready = email.trim() && password && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError("");
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full h-[52px] sm:h-[58px] rounded-full border border-[#CFCFCF] bg-[#FAFAFA] px-6 sm:px-7 text-[17px] sm:text-[19px] font-medium outline-none placeholder:text-[#B5B5B5] focus:border-[#111] transition-colors";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4" onClick={() => navigate("/")} data-testid="login-backdrop">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[460px] bg-white rounded-[28px] px-6 sm:px-8 pt-10 sm:pt-14 pb-8 sm:pb-10 shadow-[0_30px_80px_rgba(0,0,0,0.35)] df-fade-up"
        data-testid="login-modal"
      >
        <div className="text-center">
          <Logo size={46} />
        </div>
        <div className="mt-10 sm:mt-14 space-y-5 sm:space-y-6">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className={inputCls}
            autoComplete="username"
            data-testid="login-email-input"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className={inputCls}
            autoComplete="current-password"
            data-testid="login-password-input"
          />
        </div>
        {error && (
          <p className="mt-5 text-center text-[14px] font-medium text-[#B91C1C]" data-testid="login-error">
            {error}
          </p>
        )}
        <button
          type="button"
          className="block mx-auto mt-7 text-[17px] font-medium text-[#A3A3A3] hover:text-[#6B6B6B] transition-colors"
          onClick={() => toast("Password resets are managed by the site owner.")}
          data-testid="forgot-password-link"
        >
          Forgot password?
        </button>
        <button
          type="submit"
          disabled={!ready}
          className={`mt-7 w-full h-[52px] sm:h-[58px] rounded-full text-[18px] sm:text-[20px] font-semibold transition-colors duration-200 ${
            ready ? "bg-[#F8D481] text-black hover:bg-[#f3c968]" : "bg-[#EDEDEB] text-[#8A8A8A] cursor-not-allowed"
          }`}
          data-testid="login-submit-button"
        >
          {busy ? "Logging in…" : "Log In"}
        </button>
      </form>
    </div>
  );
};
